import React, { useEffect, useState, useCallback } from 'react';
import { formatCurrency, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Upload, FileText, Plus, Loader2, ChevronLeft, ChevronRight, Check, X, Edit2, Trash2, Eye, Download, Send, Calendar } from 'lucide-react';
import axios from 'axios';
import { useAuthStore } from '../store';
import { useDropzone } from 'react-dropzone';

import { API_URL } from '../lib/api';

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const formatMonthDisplay = (monthStr) => {
  if (!monthStr) return '';
  const [year, month] = monthStr.split('-');
  return `${MONTHS[parseInt(month) - 1]} ${year}`;
};

const navigateMonth = (currentMonth, direction) => {
  const [year, month] = currentMonth.split('-').map(Number);
  let newMonth = month + direction;
  let newYear = year;
  if (newMonth < 1) { newMonth = 12; newYear--; }
  if (newMonth > 12) { newMonth = 1; newYear++; }
  return `${newYear}-${String(newMonth).padStart(2, '0')}`;
};

export const ClosedServicesPage = () => {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isImportToAROpen, setIsImportToAROpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessingPDF, setIsProcessingPDF] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [extractedData, setExtractedData] = useState([]);
  const [summary, setSummary] = useState({ total: 0, count: 0, imported: 0 });
  
  const [formData, setFormData] = useState({
    budget_number: '',
    customer_name: '',
    service_description: '',
    location: '',
    period: '',
    total_value: '',
    payment_conditions: '',
    payment_due_date: '',
    observations: '',
    competence_month: getCurrentMonth()
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/finance/closed-services?month=${selectedMonth}`, { headers });
      const data = res.data.services || [];
      setServices(data);
      
      // Calculate summary
      const total = data.reduce((sum, s) => sum + (s.total_value || 0), 0);
      const imported = data.filter(s => s.imported_to_ar).length;
      setSummary({ total, count: data.length, imported });
    } catch (error) {
      console.error('Error fetching services:', error);
      // If endpoint doesn't exist yet, show empty state
      setServices([]);
      setSummary({ total: 0, count: 0, imported: 0 });
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, token]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const onDrop = useCallback(async (acceptedFiles) => {
    const pdfFiles = acceptedFiles.filter(f => f.type === 'application/pdf');
    if (pdfFiles.length === 0) {
      toast.error('Por favor, envie apenas arquivos PDF');
      return;
    }
    
    setUploadedFiles(pdfFiles);
    setIsProcessingPDF(true);
    setExtractedData([]);
    
    try {
      // Process each PDF
      for (const file of pdfFiles) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('competence_month', selectedMonth);
        
        const res = await axios.post(`${API_URL}/api/finance/closed-services/extract-pdf`, formDataUpload, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
        
        if (res.data.extracted) {
          setExtractedData(prev => [...prev, { ...res.data.extracted, filename: file.name }]);
        }
      }
      toast.success(`${pdfFiles.length} arquivo(s) processado(s)`);
    } catch (error) {
      console.error('Error processing PDF:', error);
      toast.error('Erro ao processar PDF. Tente novamente ou cadastre manualmente.');
    } finally {
      setIsProcessingPDF(false);
    }
  }, [selectedMonth, headers]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true
  });

  const handleSaveExtracted = async () => {
    if (extractedData.length === 0) return;
    setIsSubmitting(true);
    
    try {
      for (const data of extractedData) {
        await axios.post(`${API_URL}/api/finance/closed-services`, {
          ...data,
          competence_month: selectedMonth
        }, { headers });
      }
      
      toast.success(`${extractedData.length} serviço(s) cadastrado(s)!`);
      setIsUploadOpen(false);
      setExtractedData([]);
      setUploadedFiles([]);
      fetchServices();
    } catch (error) {
      toast.error('Erro ao salvar serviços');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.budget_number || !formData.customer_name || !formData.total_value) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/finance/closed-services`, {
        ...formData,
        total_value: parseFloat(formData.total_value),
        competence_month: selectedMonth
      }, { headers });
      
      toast.success('Serviço cadastrado!');
      setIsCreateOpen(false);
      setFormData({
        budget_number: '', customer_name: '', service_description: '',
        location: '', period: '', total_value: '', payment_conditions: '',
        observations: '', competence_month: getCurrentMonth()
      });
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedService) return;
    
    setIsSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/finance/closed-services/${selectedService.id}`, {
        ...formData,
        total_value: parseFloat(formData.total_value)
      }, { headers });
      
      toast.success('Serviço atualizado!');
      setIsEditOpen(false);
      setSelectedService(null);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedService) return;
    setIsSubmitting(true);
    try {
      await axios.delete(`${API_URL}/api/finance/closed-services/${selectedService.id}`, { headers });
      toast.success('Serviço excluído!');
      setIsDeleteOpen(false);
      setSelectedService(null);
      fetchServices();
    } catch (error) {
      toast.error('Erro ao excluir');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImportToAR = async () => {
    if (!selectedService) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/finance/closed-services/${selectedService.id}/import-to-ar`, {}, { headers });
      toast.success('Importado para Contas a Receber!');
      setIsImportToAROpen(false);
      setSelectedService(null);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelService = async () => {
    if (!selectedService) return;
    setIsSubmitting(true);
    try {
      const cancelReceivable = selectedService.imported_to_ar;
      await axios.post(
        `${API_URL}/api/finance/closed-services/${selectedService.id}/cancel?cancel_receivable=${cancelReceivable}`,
        {},
        { headers }
      );
      
      const message = cancelReceivable 
        ? 'Orçamento e A Receber vinculado cancelados!' 
        : 'Orçamento cancelado!';
      toast.success(message);
      setIsCancelOpen(false);
      setSelectedService(null);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditDialog = (service) => {
    setSelectedService(service);
    setFormData({
      budget_number: service.budget_number || '',
      customer_name: service.customer_name || '',
      service_description: service.service_description || '',
      location: service.location || '',
      period: service.period || '',
      total_value: String(service.total_value || ''),
      payment_conditions: service.payment_conditions || '',
      payment_due_date: service.payment_due_date || '',
      observations: service.observations || '',
      competence_month: service.competence_month || selectedMonth
    });
    setIsEditOpen(true);
  };

  const renderFormFields = () => (
    <div className="grid gap-4 py-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Nº do Orçamento *</Label>
          <Input 
            value={formData.budget_number} 
            onChange={(e) => setFormData({...formData, budget_number: e.target.value})} 
            placeholder="Ex: 17870"
            required
          />
        </div>
        <div className="space-y-2">
          <Label>Cliente *</Label>
          <Input 
            value={formData.customer_name} 
            onChange={(e) => setFormData({...formData, customer_name: e.target.value})} 
            placeholder="Nome do cliente"
            required
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Serviço Realizado</Label>
        <Textarea 
          value={formData.service_description} 
          onChange={(e) => setFormData({...formData, service_description: e.target.value})} 
          placeholder="Descrição do serviço"
          rows={2}
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Local</Label>
          <Input 
            value={formData.location} 
            onChange={(e) => setFormData({...formData, location: e.target.value})} 
            placeholder="Local do serviço/evento"
          />
        </div>
        <div className="space-y-2">
          <Label>Período</Label>
          <Input 
            value={formData.period} 
            onChange={(e) => setFormData({...formData, period: e.target.value})} 
            placeholder="Ex: 15 a 17/01/2026"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Valor Total (R$) *</Label>
          <Input 
            type="number"
            step="0.01"
            min="0"
            value={formData.total_value} 
            onChange={(e) => setFormData({...formData, total_value: e.target.value})} 
            placeholder="0,00"
            required
          />
        </div>
        <div className="space-y-2">
          <Label>Condições de Pagamento</Label>
          <Input 
            value={formData.payment_conditions} 
            onChange={(e) => setFormData({...formData, payment_conditions: e.target.value})} 
            placeholder="Ex: 50% entrada + 50% após"
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Data de Vencimento</Label>
        <Input 
          type="date"
          value={formData.payment_due_date} 
          onChange={(e) => setFormData({...formData, payment_due_date: e.target.value})} 
        />
        <p className="text-xs text-muted-foreground">Se não informado, usará a data do evento ou fim do mês de competência</p>
      </div>
      
      <div className="space-y-2">
        <Label>Observações</Label>
        <Textarea 
          value={formData.observations} 
          onChange={(e) => setFormData({...formData, observations: e.target.value})} 
          placeholder="Observações adicionais"
          rows={2}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="closed-services-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Conciliação Orçamentos Fechados</h1>
          <p className="text-muted-foreground">Controle de orçamentos aprovados e serviços confirmados</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setIsUploadOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />Importar PDF
          </Button>
          <Button onClick={() => setIsCreateOpen(true)} data-testid="new-service-btn">
            <Plus className="h-4 w-4 mr-2" />Novo Serviço
          </Button>
        </div>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setSelectedMonth(navigateMonth(selectedMonth, -1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Badge variant="outline" className="px-4 py-2 text-base font-medium">
            <Calendar className="h-4 w-4 mr-2" />
            {formatMonthDisplay(selectedMonth)}
          </Badge>
          <Button variant="outline" size="icon" onClick={() => setSelectedMonth(navigateMonth(selectedMonth, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">Total de Serviços</p>
                <p className="text-2xl font-bold">{summary.count}</p>
              </div>
              <FileText className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">Valor Total</p>
                <p className="text-2xl font-bold font-mono">{formatCurrency(summary.total)}</p>
              </div>
              <Badge variant="outline" className="text-green-600">{formatMonthDisplay(selectedMonth)}</Badge>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">Importados p/ A Receber</p>
                <p className="text-2xl font-bold">{summary.imported} / {summary.count}</p>
              </div>
              <Check className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Services Table */}
      <Card>
        <CardHeader>
          <CardTitle>Serviços do Mês</CardTitle>
          <CardDescription>Orçamentos aprovados e serviços confirmados</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">Nenhum serviço cadastrado para {formatMonthDisplay(selectedMonth)}</p>
              <div className="flex justify-center gap-2">
                <Button variant="outline" onClick={() => setIsUploadOpen(true)}>
                  <Upload className="h-4 w-4 mr-2" />Importar PDF
                </Button>
                <Button onClick={() => setIsCreateOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />Cadastrar
                </Button>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nº Orçamento</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Serviço</TableHead>
                  <TableHead>Local/Período</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {services.map((service) => (
                  <TableRow key={service.id} className={service.status === 'cancelled' ? 'opacity-50' : ''}>
                    <TableCell className="font-medium">{service.budget_number}</TableCell>
                    <TableCell>{service.customer_name}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{service.service_description || '-'}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {service.location && <div>{service.location}</div>}
                        {service.period && <div className="text-muted-foreground">{service.period}</div>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(service.total_value)}</TableCell>
                    <TableCell>
                      {service.status === 'cancelled' ? (
                        <Badge variant="destructive">Cancelado</Badge>
                      ) : service.imported_to_ar ? (
                        <Badge variant="default" className="bg-green-600">Importado</Badge>
                      ) : (
                        <Badge variant="outline">Pendente</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="icon" onClick={() => { setSelectedService(service); setIsViewOpen(true); }} title="Ver">
                          <Eye className="h-4 w-4" />
                        </Button>
                        {service.status !== 'cancelled' && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => openEditDialog(service)} title="Editar">
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            {!service.imported_to_ar && (
                              <Button variant="ghost" size="icon" onClick={() => { setSelectedService(service); setIsImportToAROpen(true); }} title="Importar p/ A Receber" className="text-green-600">
                                <Send className="h-4 w-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" className="text-amber-600 hover:text-amber-700" onClick={() => { setSelectedService(service); setIsCancelOpen(true); }} title="Cancelar Orçamento">
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700" onClick={() => { setSelectedService(service); setIsDeleteOpen(true); }} title="Excluir">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Upload PDF Dialog */}
      <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Importar Orçamentos (PDF)</DialogTitle>
            <DialogDescription>Arraste os PDFs de orçamentos confirmados para extrair automaticamente os dados</DialogDescription>
          </DialogHeader>
          
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
              ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}`}
          >
            <input {...getInputProps()} />
            <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            {isDragActive ? (
              <p className="text-primary">Solte os arquivos aqui...</p>
            ) : (
              <>
                <p className="font-medium">Arraste e solte arquivos PDF aqui</p>
                <p className="text-sm text-muted-foreground mt-1">ou clique para selecionar</p>
              </>
            )}
          </div>
          
          {isProcessingPDF && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Processando PDF com IA...</span>
            </div>
          )}
          
          {extractedData.length > 0 && (
            <div className="space-y-4">
              <h4 className="font-medium">Dados Extraídos</h4>
              {extractedData.map((data, idx) => (
                <Card key={idx} className="p-4">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-muted-foreground">Arquivo:</span> {data.filename}</div>
                    <div><span className="text-muted-foreground">Nº Orçamento:</span> {data.budget_number}</div>
                    <div><span className="text-muted-foreground">Cliente:</span> {data.customer_name}</div>
                    <div><span className="text-muted-foreground">Valor:</span> {formatCurrency(data.total_value)}</div>
                    <div><span className="text-muted-foreground">Período:</span> {data.period || '-'}</div>
                    <div><span className="text-muted-foreground">Vencimento:</span> {data.payment_due_date || 'A definir'}</div>
                    <div className="col-span-2"><span className="text-muted-foreground">Serviço:</span> {data.service_description}</div>
                    <div className="col-span-2"><span className="text-muted-foreground">Condições:</span> {data.payment_conditions || '-'}</div>
                  </div>
                </Card>
              ))}
            </div>
          )}
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setIsUploadOpen(false); setExtractedData([]); setUploadedFiles([]); }}>
              Cancelar
            </Button>
            {extractedData.length > 0 && (
              <Button onClick={handleSaveExtracted} disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Salvar {extractedData.length} Serviço(s)
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Novo Serviço Fechado</DialogTitle>
            <DialogDescription>Cadastre manualmente um orçamento aprovado</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            {renderFormFields()}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Cadastrar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar Serviço</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit}>
            {renderFormFields()}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Detalhes do Serviço</DialogTitle>
          </DialogHeader>
          {selectedService && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">Nº Orçamento</span><p className="font-medium text-lg">{selectedService.budget_number}</p></div>
                <div><span className="text-muted-foreground">Valor</span><p className="font-medium text-lg font-mono">{formatCurrency(selectedService.total_value)}</p></div>
                <div className="col-span-2"><span className="text-muted-foreground">Cliente</span><p className="font-medium">{selectedService.customer_name}</p></div>
                {selectedService.service_description && <div className="col-span-2"><span className="text-muted-foreground">Serviço</span><p className="font-medium">{selectedService.service_description}</p></div>}
                {selectedService.location && <div><span className="text-muted-foreground">Local</span><p className="font-medium">{selectedService.location}</p></div>}
                {selectedService.period && <div><span className="text-muted-foreground">Período</span><p className="font-medium">{selectedService.period}</p></div>}
                {selectedService.payment_conditions && <div className="col-span-2"><span className="text-muted-foreground">Cond. Pagamento</span><p className="font-medium">{selectedService.payment_conditions}</p></div>}
                {selectedService.observations && <div className="col-span-2"><span className="text-muted-foreground">Observações</span><p className="font-medium">{selectedService.observations}</p></div>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewOpen(false)}>Fechar</Button>
            {!selectedService?.imported_to_ar && (
              <Button onClick={() => { setIsViewOpen(false); setIsImportToAROpen(true); }} className="bg-green-600 hover:bg-green-700">
                <Send className="h-4 w-4 mr-2" />Importar p/ A Receber
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import to AR Confirmation */}
      <AlertDialog open={isImportToAROpen} onOpenChange={setIsImportToAROpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Importar para Contas a Receber</AlertDialogTitle>
            <AlertDialogDescription>
              Deseja criar uma conta a receber a partir do orçamento <strong>{selectedService?.budget_number}</strong>?
              <div className="mt-2 p-3 bg-muted rounded-lg">
                <p><strong>Cliente:</strong> {selectedService?.customer_name}</p>
                <p><strong>Valor:</strong> {formatCurrency(selectedService?.total_value || 0)}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleImportToAR} disabled={isSubmitting} className="bg-green-600 hover:bg-green-700">
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmar Importação
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Serviço</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o serviço <strong>{selectedService?.budget_number}</strong>?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Confirmation */}
      <AlertDialog open={isCancelOpen} onOpenChange={setIsCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar Orçamento</AlertDialogTitle>
            <AlertDialogDescription>
              Deseja cancelar o orçamento <strong>{selectedService?.budget_number}</strong>?
              {selectedService?.imported_to_ar && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
                  <strong>⚠️ Atenção:</strong> Este orçamento possui um <strong>A Receber vinculado</strong>. 
                  Ao cancelar, o A Receber também será cancelado automaticamente.
                </div>
              )}
              <p className="mt-3 text-sm">
                O orçamento ficará marcado como cancelado e não poderá mais ser importado.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelService} className="bg-amber-600 hover:bg-amber-700" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmar Cancelamento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ClosedServicesPage;
